// Date helpers shared by the engine and the client (menu previews show the
// same fire dates the server will record). Users live in their own
// timezone (a stored setting, defaulting to UTC): "today" and snooze fire
// dates are computed there, never in server time.
import type { SnoozeDuration } from "./ontology.js";

// YYYY-MM-DD in the given IANA timezone.
export function todayInTz(tz: string, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function addMonths(date: string, months: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + months);
  // Clamp instead of rolling over (31 Jan + 1 month is 28 Feb, not 3 Mar).
  if (d.getUTCDate() !== day) d.setUTCDate(0);
  return d.toISOString().slice(0, 10);
}

// Astronomical season boundaries, to the nearest civil day; good enough
// for "when the season changes".
const SEASON_STARTS: Array<[month: number, day: number]> = [
  [3, 20],
  [6, 21],
  [9, 22],
  [12, 21],
];

export function nextSeasonChange(date: string): string {
  const y = Number(date.slice(0, 4));
  const pad = (n: number) => String(n).padStart(2, "0");
  const candidates = SEASON_STARTS.map(([m, d]) => `${y}-${pad(m)}-${pad(d)}`);
  candidates.push(`${y + 1}-03-20`);
  return candidates.find((c) => c > date) ?? date;
}

// "Later this year" means the year's quiet end; close to or past it, the
// answer rolls forward so it is always genuinely later.
export function laterThisYear(date: string): string {
  const y = Number(date.slice(0, 4));
  const target = `${y}-12-01`;
  return target > addDays(date, 20) ? target : `${y + 1}-12-01`;
}

// Later, with a real duration. Snoozed items come back exactly once, on
// their date.
export function fireDateFor(duration: SnoozeDuration, today: string): string {
  switch (duration) {
    case "week":
      return addDays(today, 7);
    case "month":
      return addMonths(today, 1);
    case "quarter":
      return addMonths(today, 3);
    case "season":
      return nextSeasonChange(today);
    case "year":
      return laterThisYear(today);
  }
}

// ISO week id like 2026-W31, computed in the user's timezone.
export function isoWeekOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const year = d.getUTCFullYear();
  const yearStart = Date.UTC(year, 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart) / 86400000 + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

// Monday of the ISO week containing the date.
export function weekStart(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

